import React, {useState} from 'react';
import {View, Text, FlatList, Image} from 'react-native';
import moment from 'moment';
import type {
  RecentOrderType,
  RecentOrderItemType,
} from 'src/types/RecentOrders';
import Styles from '../../../Styles';
import AVFormattedCurrency from '../../AVFormattedCurrency';
import Pressable from '../../AVTouchableOpacity';
import {WithLocalizedProps} from 'src/types/PropTypes';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
import ChevronUp from 'src/components/img/svg/ChevronUp';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {calculateTotal, getMenuItemUrl} from './recentOrderUtils';
import RecentOrderItem from './RecentOrderItem';
import {styles} from './recentOrderStyles';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import {getDescriber} from 'src/components/elements/orderAhead/descriptor/DescriptorType';

const {recentOrdersHideDetailsColors} = getDescriber();

type RecentOrderProps = WithLocalizedProps & {
  recentOrder: RecentOrderType;
  addAllItems: (order: RecentOrderType) => void;
  addItem: (item: RecentOrderItemType) => void;
  productCellPressed: (item: RecentOrderItemType) => void;
};

export default ({
  strings,
  recentOrder,
  addAllItems,
  addItem,
  productCellPressed,
}: RecentOrderProps) => {
  const hasAddAllButton =
    recentOrder.items.filter((item) => item.available === true).length > 0;
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const getTimeAndDate = (dateString: string) => {
    return moment(dateString).format('MM/DD/YY');
  };

  const getShowHide = (numOfItems: number) => {
    if (expanded) {
      return getHideText(numOfItems);
    } else {
      return getShowText(numOfItems);
    }
  };

  const getHideText = (numOfItems: number) => {
    let displayText = '';
    if (numOfItems > 1) {
      displayText = Localized.Labels.formatString(
        Localized.Labels.hide_details_1,
        numOfItems,
      ) as string;
    } else {
      displayText = Localized.Labels.hide_details_0;
    }
    return displayText;
  };

  const getShowText = (numOfItems: number) => {
    let displayText = '';
    if (numOfItems > 1) {
      displayText = Localized.Labels.formatString(
        Localized.Labels.show_details_1,
        numOfItems,
      ) as string;
    } else {
      displayText = Localized.Labels.show_details_0;
    }
    return displayText;
  };

  const getRecentOrderSubItems = () => {
    return recentOrder.items.length;
  };

  const getSubItems = () => {
    return recentOrder.items;
  };

  const isSingleCartItem = () => {
    return recentOrder.items.length == 1;
  };

  return (
    <View
      style={[getLayoutDescriber().shadowStyles().shadow, styles.container]}
    >
      <View>
        <View style={styles.titleBar}>
          <View>
            {getMenuItemUrl(recentOrder.items[0].scancode) == null ? (
              <View style={styles.defaultImageContainer}>
                <Image
                  accessible
                  accessibilityLabel={'Default-MenuIcon Image'}
                  accessibilityRole="image"
                  aria-label="Default-MenuIcon"
                  role="img"
                  style={styles.parentListImage}
                  source={require('src/components/img/DefaultMenuItem.png')}
                  resizeMode="cover"
                />
              </View>
            ) : (
              <View style={styles.imageContainer}>
                <Image
                  accessible={true}
                  accessibilityElementsHidden={true}
                  importantForAccessibility="no-hide-descendants"
                  accessibilityRole="image"
                  role="img"
                  source={{
                    uri: getMenuItemUrl(recentOrder.items[0].scancode),
                  }}
                  style={[styles.parentImage]}
                  resizeMode="cover"
                  testID="productImage"
                />
              </View>
            )}
          </View>
          <View style={styles.titleContainer}>
            <Text
              style={styles.titleText}
              numberOfLines={2}
              ellipsizeMode="tail"
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              nativeID={`Recent_Order_${recentOrder.items[0].name}`}
            >
              {recentOrder.items[0].name}
            </Text>
            <Text
              style={styles.dateText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              nativeID={`Recent_Order_${getTimeAndDate(recentOrder.transdate)}`}
            >
              {getTimeAndDate(recentOrder.transdate)}
            </Text>
            {
              <Pressable
                onPress={toggleExpand}
                nativeID={`Btn_Recent_Order_${getShowHide(
                  getRecentOrderSubItems(),
                )}`}
              >
                <View style={styles.showHideUnderLine}>
                  <Text
                    style={styles.showHideText}
                    numberOfLines={1}
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                    nativeID={`Recent_Order_${getShowHide(
                      getRecentOrderSubItems(),
                    )}`}
                  >
                    {getShowHide(getRecentOrderSubItems())}
                  </Text>
                </View>
              </Pressable>
            }
          </View>
          <View>
            {hasAddAllButton ? (
              <RoundedAppButton
                buttonType={RoundedAppButtonType.Outline}
                titleText={Localized.Buttons.reoder_recent_menu}
                onPress={() => addAllItems(recentOrder)}
                buttonContainerStyle={{
                  marginTop: Styles.Spacing.m2,
                  alignSelf: 'center',
                }}
                buttonViewStyle={{
                  height: 32,
                }}
                accessibilityLabelValue="Reorder"
              />
            ) : (
              <Text
                style={styles.unavailable}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                nativeID={`Recent_Order_${strings.Labels.unavailable}`}
              >
                {strings.Labels.unavailable}
              </Text>
            )}
            <AVFormattedCurrency
              style={styles.itemPrice}
              value={calculateTotal(recentOrder.items)}
              accessibilityLabel="price"
              currency={AccountStore.getCurrency()}
            />
          </View>
        </View>
      </View>
      {expanded && (
        <View>
          <View
            style={{
              borderBottomColor: Styles.Colors.PayNew.neutralHuesBase09,
              borderBottomWidth: 1,
              marginHorizontal: Styles.Spacing.m2,
            }}
          />
          <FlatList
            data={getSubItems()}
            keyExtractor={(item) => item.id}
            renderItem={({item}) => {
              return (
                <RecentOrderItem
                  addItem={addItem}
                  singleCartItem={isSingleCartItem()}
                  strings={strings}
                  orderItem={item}
                  productCellPressed={productCellPressed}
                  hasAddAllButton={hasAddAllButton}
                />
              );
            }}
          />
          <Pressable
            style={{alignSelf: 'center', marginBottom: Styles.Spacing.m3}}
            onPress={toggleExpand}
            nativeID={'Btn' + Localized.Labels.hide_details}
          >
            <View
              style={{
                flexDirection: 'row',
              }}
            >
              <View style={styles.showHideUnderLine}>
                <Text
                  style={styles.hideText}
                  numberOfLines={1}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                >
                  {Localized.Labels.hide_details}
                </Text>
              </View>
              <View
                style={{
                  marginTop: Styles.Spacing.m1,
                  marginLeft: Styles.Spacing.m2,
                }}
              >
                <ChevronUp color={recentOrdersHideDetailsColors()} />
              </View>
            </View>
          </Pressable>
        </View>
      )}
    </View>
  );
};
